import {compact} from '@benchling/js-utils';

import AntibodyRegTableColumnTransformer from 'antibodies/registration-table/AntibodyRegTableColumnTransformer';
import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {assert} from 'Asserts';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import OligoConjugateRegTableColumnTransformer from 'modbio/complex-polymers/oligo-conjugates/OligoConjugateRegTableColumnTransformer';
import OligoDuplexRegTableColumnTransformer from 'modbio/complex-polymers/oligo-duplexes/OligoDuplexRegTableColumnTransformer';
import AdcRegTableColumnTransformer from 'molbio/adcs/registration-table/AdcRegTableColumnTransformer';
import {SSI_ADC_INTERFACE_ID} from 'molbio/adcs/schema-configuration/AdcSchemaInterfaceConstants';
import {ImplementedStaticSchemaInterfaceGQL} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {CustomEvalFnAndType} from 'notebook/tables/constants/CustomEvalConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import * as logging from 'util/Logging';

export interface RegistrationTableRowData {
  bioentityExists: boolean;
}

/**
 * Base interface for classes that alter registration table columns based on the implementation of particular schema interfaces.
 *
 * Implementers should add an instantiation of their class to the COLUMN_TRANSFORMERS array.
 */
export interface SchemaInterfaceColumnTransformerBase {
  /**
   * Replace an existing column in the registration table with a new column based on the implemented schema interface.
   *
   * If no replacement is required, return null.
   */
  replaceExistingColumn: (
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ) => StructuredTableColumn | null;

  /**
   * Add net new columns to the registration table based on the implemented schema interface.
   */
  getNewColumns: (
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ) => Array<StructuredTableColumn>;

  /**
   * Whether or not to hide a given column in the registration table.
   */
  shouldHideColumn: (
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ) => boolean;

  /**
   * Get front end metadata to a column based on the implemented schema interface.
   */
  getFrontEndMetadata: (
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ) => unknown | null;

  /**
   * Finalize a column's configuration on load, returning the fully-configured column or null to leave it
   * unchanged. Unlike getFrontEndMetadata (which only contributes a `frontEndMetadata` blob), this may set
   * top-level column fields (e.g. `bioentityTypeFilters`). Runs on every load, so non-persisted config must
   * be re-derived here. Transformers that omit it fall back to applying getFrontEndMetadata.
   */
  configureColumn?: (
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ) => StructuredTableColumn | null;

  /**
   * The custom eval that validates a column's cells, or null to leave the column to the registration table's
   * default evaluation. Transformers that omit it add no custom evals.
   */
  getCustomEval?: (
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ) => CustomEvalFnAndType<unknown> | null;
}

const COLUMN_TRANSFORMERS: {[interfaceId: string]: SchemaInterfaceColumnTransformerBase} = {
  [ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID.toString()]: new OligoConjugateRegTableColumnTransformer(),
  [ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID.toString()]: new OligoDuplexRegTableColumnTransformer(),
  [ANTIBODY_SCHEMA_INTERFACE_ID]: new AntibodyRegTableColumnTransformer(),
  [SSI_ADC_INTERFACE_ID]: new AdcRegTableColumnTransformer(),
};

// ---------------------------------------------------------------------------------------------------
// Orchestration: applies the registered transformers to a registration table's columns.
//   - transformColumnsForInterfaces: builds the column list (replace -> hide -> add), during column
//     construction (called from StructuredTableUtilsForRegistration).
//   - configureColumnForInterfaces: finalizes a single column's config on load, during metadata
//     injection (called from RegistrationStructuredTableAdapter.injectFrontEndMetadata).
//   - getCustomEvalForInterfaces: looks up a column's custom eval during evaluation (called from
//     RegistrationStructuredTableAdapter.getTableSpecificCustomEvalFn).
// All assume at most one implemented interface acts on a given column (see the assertions/warnings).
// ---------------------------------------------------------------------------------------------------

/**
 * Transforms the existing columns in a registration table based on the implemented schema interfaces.
 * Expects that no two interfaces will attempt to replace/transform the same column.
 */
export function transformColumnsForInterfaces(
  existingColumns: Array<StructuredTableColumn>,
  implementedSchemaInterfaces: Array<ImplementedStaticSchemaInterfaceGQL>,
  tableRowData: RegistrationTableRowData
): Array<StructuredTableColumn> {
  const transformers = compact(
    implementedSchemaInterfaces.map((implementedInterface) =>
      COLUMN_TRANSFORMERS[implementedInterface.schemaInterface.id]
        ? {transformer: COLUMN_TRANSFORMERS[implementedInterface.schemaInterface.id], implementedInterface}
        : null
    )
  );
  const columnsWithTransformedFlag = existingColumns.map((column) => {
    const columnTransforms = compact(
      transformers.map(({transformer, implementedInterface}) =>
        transformer.replaceExistingColumn(column, implementedInterface, tableRowData)
      )
    );
    if (columnTransforms.length === 1) {
      const newColumn = columnTransforms[0];
      return {column: newColumn, transformed: true};
    }
    if (columnTransforms.length >= 1) {
      logging.warn(`Multiple schema interfaces attempted to transform column ${column.name}`);
    }
    return {column, transformed: false};
  });
  // Only filter out columns which haven't been transformed
  const filteredTransformedColumns = columnsWithTransformedFlag
    .filter(({column, transformed}) => {
      if (
        transformers.some(({transformer, implementedInterface}) =>
          transformer.shouldHideColumn(column, implementedInterface)
        )
      ) {
        if (transformed) {
          logging.warn(`Already transformed column ${column.name} is being hidden by another interface`);
        }
        return false;
      }
      return true;
    })
    .map(({column}) => column);
  const newColumns = transformers.flatMap(({transformer, implementedInterface}) =>
    transformer.getNewColumns(implementedInterface, tableRowData)
  );
  // Insert new columns before the first schema field column if it exists, otherwise append to the end. This
  // mirrors the logic in toStructuredTableColumns where we list the existing bioentity column, then the
  // domain specific columns (e.g. bases), and then schema field columns
  const firstSchemaFieldColumnIndex = filteredTransformedColumns.findIndex(
    (column) => column.columnType === TableColumnType.TAG_VALUE
  );
  const insertionIndex =
    firstSchemaFieldColumnIndex !== -1 ? firstSchemaFieldColumnIndex : filteredTransformedColumns.length;

  return filteredTransformedColumns.toSpliced(insertionIndex, 0, ...newColumns);
}

/**
 * Finalizes a single column's configuration using whichever implemented interface configures it, returning
 * the reconfigured column or null if none applies. Expects at most one interface to configure a given column.
 */
export function configureColumnForInterfaces(
  column: StructuredTableColumn,
  implementedSchemaInterfaces: Array<ImplementedStaticSchemaInterfaceGQL>
): StructuredTableColumn | null {
  const configuredColumns = compact(
    implementedSchemaInterfaces.map((implementedInterface) => {
      const transformer = COLUMN_TRANSFORMERS[implementedInterface.schemaInterface.id];
      return transformer != null
        ? configureColumnWithTransformer(transformer, column, implementedInterface)
        : null;
    })
  );
  if (configuredColumns.length === 0) {
    return null;
  }

  assert(configuredColumns.length === 1, 'Multiple schema interfaces attempted to configure the same column');
  return configuredColumns[0];
}

/**
 * Returns the custom eval for a column from whichever implemented interface supplies one, or null if none
 * does. Expects at most one interface to supply a custom eval for a given column.
 */
export function getCustomEvalForInterfaces(
  column: StructuredTableColumn,
  implementedSchemaInterfaces: Array<ImplementedStaticSchemaInterfaceGQL>
): CustomEvalFnAndType<unknown> | null {
  const customEvals = compact(
    implementedSchemaInterfaces.map(
      (implementedInterface) =>
        COLUMN_TRANSFORMERS[implementedInterface.schemaInterface.id]?.getCustomEval?.(
          column,
          implementedInterface
        ) ?? null
    )
  );
  if (customEvals.length === 0) {
    return null;
  }

  assert(
    customEvals.length === 1,
    'Multiple schema interfaces attempted to supply a custom eval for the same column'
  );
  return customEvals[0];
}

/**
 * Applies a transformer's configureColumn, falling back to its getFrontEndMetadata for transformers that only
 * contribute a `frontEndMetadata` blob.
 */
function configureColumnWithTransformer(
  transformer: SchemaInterfaceColumnTransformerBase,
  column: StructuredTableColumn,
  implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
): StructuredTableColumn | null {
  if (transformer.configureColumn != null) {
    return transformer.configureColumn(column, implementedSchemaInterface);
  }
  const frontEndMetadata = transformer.getFrontEndMetadata(column, implementedSchemaInterface);
  return frontEndMetadata != null ? {...column, frontEndMetadata} : null;
}
